import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { http } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { LOGO } from "../lib/site";
import { EnquiriesPanel } from "../components/admin/EnquiriesPanel";
import { ContentPanel } from "../components/admin/ContentPanel";
import { SettingsPanel } from "../components/admin/SettingsPanel";

const TABS = ["Enquiries", "Products", "Installations", "Articles", "Website"];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("Enquiries");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useSeo({ title: "MAKO Admin" });

  useEffect(() => {
    http
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => navigate("/admin/login"));
    http.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [navigate, tab]);

  const logout = async () => {
    await http.post("/auth/logout");
    navigate("/admin/login");
  };

  if (!user) return <div className="min-h-screen bg-[#F5F5F2]" data-testid="admin-loading" />;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F5F5F2]">
      <header className="border-b border-[#111111]/10 bg-white">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-6 py-4 lg:px-10">
          <Link to="/">
            <img src={LOGO.primary} alt="MAKO Mats" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-6">
            <span className="hidden text-xs text-[#7A7F85] sm:block">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-[#111111]/15 px-4 py-2.5 text-[0.625rem] font-bold uppercase tracking-[0.14em] text-[#111111] transition-colors duration-300 hover:border-[#0F4C81] hover:text-[#0F4C81]"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1500px] px-6 py-10 lg:px-10 lg:py-14">
        <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-[#111111] sm:text-3xl">
          Admin
        </h1>

        {stats && (
          <div className="mt-8 grid gap-px border border-[#111111]/10 bg-[#111111]/10 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Enquiries", stats.enquiries_total],
              ["New", stats.by_status?.New || 0],
              ["Quoted", stats.by_status?.Quoted || 0],
              ["Won", stats.by_status?.Won || 0],
              ["Products", stats.products],
              ["Articles", stats.articles],
            ].map(([label, value]) => (
              <div key={label} className="bg-white p-6">
                <p className="mako-label text-[#7A7F85]">{label}</p>
                <p className="mt-3 font-display text-2xl font-bold text-[#111111]">{value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-2 border-b border-[#111111]/10 pb-5">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-[0.625rem] font-bold uppercase tracking-[0.16em] transition-colors duration-300 ${
                tab === t ? "bg-[#111111] text-white" : "text-[#3B3B3B] hover:text-[#0F4C81]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {tab === "Enquiries" && <EnquiriesPanel />}
          {tab === "Products" && <ContentPanel collection="products" label="Products" />}
          {tab === "Installations" && (
            <ContentPanel
              collection="installations"
              label="Installations"
              newTemplate={{
                facility: "New facility",
                location: "City, India",
                sport: "MMA",
                products: ["MAKO Rollout"],
                area: "On request",
                summary: "Project summary",
                image: "",
                gallery: [],
                order: 99,
              }}
            />
          )}
          {tab === "Articles" && (
            <ContentPanel
              collection="articles"
              label="Articles"
              newTemplate={{
                title: "New article",
                category: "Gym Design",
                excerpt: "Short summary for listings and search results.",
                image: "",
                read_time: "4 min read",
                body: [{ h: "Heading", p: "Paragraph copy." }],
                order: 99,
              }}
            />
          )}
          {tab === "Website" && <SettingsPanel />}
        </div>
      </div>
    </div>
  );
}
